'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
import { useMemo } from 'react';
import type { ServiceOfferingFieldsFragment, ServiceTierFieldsFragment } from '@/modules/__generated__/graphql/switchboard-generated';
import { computeGrandSetupTotal, computeTierHeaderPriceWithBreakdown, getPriceBreakdown } from '@/modules/service-purchase/lib/price-breakdown-utils';
import { formatPrice } from '@/modules/service-purchase/lib/utils';
import { usePricingCalculatorContext } from '@/modules/service-purchase/providers/pricing-calculator-provider';
import { useAllOptionGroups, useHoveredTier } from '@/modules/service-purchase/providers/service-purchase-store-provider';
const CUSTOM_PRICING_LABEL = 'Custom';
interface GrandTotalRowCatalogProps {
  selectedPlan?: string;
  tiers: ServiceTierFieldsFragment[];
  offering: ServiceOfferingFieldsFragment;
}
function GrandTotalRowCatalog(t0) {
  const $ = _c(51);
  const {
    selectedPlan,
    tiers,
    offering
  } = t0;
  const {
    tierNames,
    selectedBillingCycle
  } = usePricingCalculatorContext();
  const hoveredPlan = useHoveredTier();
  const storeOptionGroups = useAllOptionGroups();
  let t1;
  if ($[0] !== storeOptionGroups) {
    t1 = new Set(storeOptionGroups.filter(_temp).map(_temp2));
    $[0] = storeOptionGroups;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeGroupIds = t1;
  let totals;
  if ($[2] !== activeGroupIds || $[3] !== offering || $[4] !== selectedBillingCycle || $[5] !== tiers) {
    totals = {};
    for (const tier of tiers) {
      if (tier.isCustomPricing) {
        totals[tier.id] = CUSTOM_PRICING_LABEL;
        continue;
      }
      const monthlyTotal = computeTierHeaderPriceWithBreakdown(offering, tier.id, selectedBillingCycle, activeGroupIds);
      totals[tier.id] = `${formatPrice(monthlyTotal, tier.pricing.currency)}/mo`;
    }
    $[2] = activeGroupIds;
    $[3] = offering;
    $[4] = selectedBillingCycle;
    $[5] = tiers;
    $[6] = totals;
  } else {
    totals = $[6];
  }
  const planTotals = totals;
  let totals_0;
  if ($[7] !== activeGroupIds || $[8] !== offering || $[9] !== selectedBillingCycle || $[10] !== tiers) {
    totals_0 = {};
    for (const tier_0 of tiers) {
      const breakdown = getPriceBreakdown(offering, tier_0.id, selectedBillingCycle, activeGroupIds);
      const setupAmount = computeGrandSetupTotal(breakdown);
      totals_0[tier_0.id] = {
        amount: setupAmount,
        label: formatPrice(setupAmount, tier_0.pricing.currency)
      };
    }
    $[7] = activeGroupIds;
    $[8] = offering;
    $[9] = selectedBillingCycle;
    $[10] = tiers;
    $[11] = totals_0;
  } else {
    totals_0 = $[11];
  }
  const setupTotals = totals_0;
  const hasSetupFees = Object.values(setupTotals).some(_temp3);
  const t2 = `4fr repeat(${tierNames.length}, minmax(144px, 1fr))`;
  const t3 = `4fr repeat(${tierNames.length}, minmax(220px, 1fr))`;
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = {
      "--grid-cols-lg": t2,
      "--grid-cols-xl": t3
    };
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const gridStyle = t4 as React.CSSProperties;
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("bg-background border-primary/30 rounded-xl border shadow-[0px_2px_10px_2px_rgba(122,58,255,0.2)]", "overflow-hidden");
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== gridStyle || $[17] !== hasSetupFees || $[18] !== hoveredPlan || $[19] !== selectedPlan || $[20] !== setupTotals || $[21] !== tierNames) {
    t6 = hasSetupFees && <div className={cn("border-border grid min-h-12 grid-cols-2 items-center border-b", "lg:grid-cols-(--grid-cols-lg) xl:grid-cols-(--grid-cols-xl)")} style={gridStyle}><span className={cn("bg-background text-foreground flex items-center px-4 text-lg/6 font-bold lg:px-6", "sticky left-0 z-10 lg:static")}>{"Setup & formation"}</span><div className={cn("flex h-full min-w-0 items-center justify-center gap-2 px-4 transition-colors lg:hidden", selectedPlan ? "bg-primary/30" : "bg-background")}>{selectedPlan && setupTotals[selectedPlan].amount > 0 && <span className="text-primary dark:text-primary-foreground text-lg/6 font-bold whitespace-nowrap">One-time: <span className="font-semibold">{setupTotals[selectedPlan].label}</span></span>}</div>{tierNames.map(plan => {
        const isActive = selectedPlan === plan;
        const setup = setupTotals[plan];
        return <div key={plan} className={cn("hidden h-full min-w-0 items-center justify-center px-6 transition-colors lg:flex", isActive ? "bg-primary/30" : "bg-background", hoveredPlan === plan && !isActive && "bg-accent/80")}>{isActive && setup.amount > 0 && <span className="text-primary dark:text-primary-foreground flex min-w-0 flex-wrap items-center justify-center gap-x-2 text-center"><span className="text-sm/5 font-bold xl:text-lg/6">One-time:</span><span className="text-sm/5 font-semibold xl:text-lg/6">{setup.label}</span></span>}</div>;
      })}</div>;
    $[16] = gridStyle;
    $[17] = hasSetupFees;
    $[18] = hoveredPlan;
    $[19] = selectedPlan;
    $[20] = setupTotals;
    $[21] = tierNames;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("grid h-12 grid-cols-2 items-center", "lg:grid-cols-(--grid-cols-lg) xl:grid-cols-(--grid-cols-xl)");
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className={cn("bg-background text-foreground flex items-center px-4 text-lg/6 font-bold lg:px-6", "sticky left-0 z-10 lg:static")}>Grand Total (Recurring)</span>;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const t9 = selectedPlan ? "bg-primary/30" : "bg-background";
  let t10;
  if ($[25] !== t9) {
    t10 = cn("flex h-full min-w-0 items-center justify-center px-4 transition-colors lg:hidden", t9);
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = selectedPlan ? "text-primary dark:text-primary-foreground" : "text-foreground";
  let t12;
  if ($[27] !== t11) {
    t12 = cn("text-xl font-bold", t11);
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = (selectedPlan && planTotals[selectedPlan]) ?? "\u2014";
  let t14;
  if ($[29] !== t12 || $[30] !== t13) {
    t14 = <span className={t12}>{t13}</span>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t10 || $[33] !== t14) {
    t15 = <div className={t10}>{t14}</div>;
    $[32] = t10;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== hoveredPlan || $[36] !== planTotals || $[37] !== selectedPlan || $[38] !== tierNames) {
    let t17;
    if ($[40] !== hoveredPlan || $[41] !== planTotals || $[42] !== selectedPlan) {
      t17 = plan_0 => {
        const isActive_0 = selectedPlan === plan_0;
        return <div key={plan_0} className={cn("hidden h-full min-w-0 items-center justify-center px-6 transition-colors lg:flex", isActive_0 ? "bg-primary/30" : "bg-background", hoveredPlan === plan_0 && !isActive_0 && "bg-accent/80")}><span className={cn("text-lg/6 font-bold", isActive_0 ? "text-primary dark:text-primary-foreground" : "text-foreground")}>{planTotals[plan_0]}</span></div>;
      };
      $[40] = hoveredPlan;
      $[41] = planTotals;
      $[42] = selectedPlan;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    t16 = tierNames.map(t17);
    $[35] = hoveredPlan;
    $[36] = planTotals;
    $[37] = selectedPlan;
    $[38] = tierNames;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[44] !== gridStyle || $[45] !== t15 || $[46] !== t16) {
    t17 = <div className={t7} style={gridStyle}>{t8}{t15}{t16}</div>;
    $[44] = gridStyle;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t17 || $[49] !== t6) {
    t18 = <div className={t5}>{t6}{t17}</div>;
    $[48] = t17;
    $[49] = t6;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  return t18;
}
function _temp3(t) {
  return t.amount > 0;
}
function _temp2(g_0) {
  return g_0.id;
}
function _temp(g) {
  return g.isSelected;
}
export { GrandTotalRowCatalog };